import { authStateAtom } from '@/auth/authStateAtom';
import { reportError } from '@/utils/tracking';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import {
    fromDurableFilterBlob,
    isCurrentDurableFilterBlob,
    stableBlobKey,
    toDurableFilterBlob,
} from './durableFilterBlob';
import {
    clearSyncRecord,
    readSyncRecord,
    toRowTimestamp,
    writeSyncRecord,
} from './durableFilterSyncRecords';
import {
    DurableFilterRowQuery,
    DurableFilterRowQueryVariables,
    SaveDurableFilterRowMutation,
    SaveDurableFilterRowMutationVariables,
} from './useDurableFilterRow.gql.types';

// How long a selection sits still before it is written, so dragging through a
// list of tags produces one save rather than one per tag.
const AUTOSAVE_DELAY_MS = 1000;

// The row holding a page's last used selection carries no name. Named rows are
// presets, which nothing here reads or writes.
const LAST_USED_FILTER_NAME = '';

const DURABLE_FILTER_ROW_QUERY = gql`
    query DurableFilterRow($pageName: String!, $filterName: String!) {
        dashboardFilterConfigs(
            filters: { pageName: { exact: $pageName }, filterName: { exact: $filterName } }
        ) {
            id
            filterBlob
            updatedAt
        }
    }
`;

const SAVE_DURABLE_FILTER_ROW_MUTATION = gql`
    mutation SaveDurableFilterRow($pageName: NonEmptyString!, $filterBlob: JSON!) {
        saveDashboardFilterConfig(input: { pageName: $pageName, filterBlob: $filterBlob }) {
            id
            filterBlob
            updatedAt
        }
    }
`;

type DurableFilterRowOptions<Value> = {
    /** Names the stored row, and is the browser storage key that row mirrors. */
    storageKey: string;
    storage: Storage;
    value: Value;
    setValue: (value: Value) => void;
    /** The unfiltered value, which a selection belonging to nobody is reset to. */
    emptyValue: Value;
    /**
     * Validates a value read back from the row. Declare it at module scope: it
     * is read on identity, so a schema rebuilt each render re-runs the read.
     */
    schema: z.ZodType<Value>;
    enabled?: boolean;
};

/**
 * Mirrors a browser-storage-backed filter selection into a backend row, so a
 * selection made in one browser reaches the next one the user opens.
 *
 * The browser storage stays the live layer: it is read synchronously on the
 * first render, and it is what a refresh reads. The row is read on every mount
 * and adopted only when it was written more recently than the row this
 * browser's copy came from, so a page opened on the newest selection does not
 * move, and one opened on a stale selection catches up.
 *
 * The browser's copy is recorded against the user who made it. A copy
 * belonging to somebody else, and a copy the atom still holds in memory after
 * its storage key has gone, are both dropped rather than shown or written:
 * clearing storage raises no `storage` event in the tab that cleared it, so
 * neither one is something the atom can notice on its own.
 *
 * Every read, write, and reset here is behind `enabled`. With it off the hook
 * touches nothing, and the browser storage behaves exactly as it did before.
 */
export const useDurableFilterRow = <Value>({
    storageKey,
    storage,
    value,
    setValue,
    emptyValue,
    schema,
    enabled = true,
}: DurableFilterRowOptions<Value>) => {
    const authState = useAtomValue(authStateAtom);
    const ownerId = authState === null ? null : authState.uuid ?? authState.email;
    const isMirroring = enabled && ownerId !== null;

    // What the browser held when the page mounted, and whether that copy is
    // this user's to keep. Captured once, since everything below changes it.
    const [mountState] = useState(() => {
        const stored = storage.getItem(storageKey);
        const record = readSyncRecord(storage, storageKey);
        const isForeign = stored !== null && record !== null && record.owner !== ownerId;
        const isOrphaned = stored === null && stableBlobKey(value) !== stableBlobKey(emptyValue);
        const keepsLocal = stored !== null && !isForeign;

        return {
            isForeign,
            isOrphaned,
            keepsLocal,
            claimsLocal: keepsLocal && record === null,
            syncedAt:
                keepsLocal && record?.syncedAt !== undefined
                    ? toRowTimestamp(record.syncedAt)
                    : null,
            key: keepsLocal ? stableBlobKey(value) : null,
        };
    });

    // When the row this browser's copy came from was written, or null for a
    // copy that never came from one.
    const syncedAtRef = useRef(mountState.syncedAt);
    // The selection last read from or written to the row. A value matching it
    // is already stored, so it is not written again.
    const syncedKeyRef = useRef(mountState.key);
    // The selection a debounce is holding, so hiding or unmounting the tab
    // writes it rather than dropping it.
    const pendingRef = useRef<Value | null>(null);
    // Whether the user has filtered since the page opened. Their selection is
    // the one on screen, so no row is adopted over it and it is written even
    // if the row has not been read yet.
    const hasEditedRef = useRef(false);
    // The selection as this hook last set or last saw it, so a change it did
    // not make reads as the user filtering.
    const lastSeenKeyRef = useRef(mountState.key ?? stableBlobKey(value));

    // Runs ahead of every effect below that sets the value, so those effects
    // can record what they are about to set and have it read as this hook's
    // doing rather than as the user filtering.
    useEffect(() => {
        const key = stableBlobKey(value);
        if (key === lastSeenKeyRef.current) {
            return;
        }

        lastSeenKeyRef.current = key;
        hasEditedRef.current = true;
    }, [value]);

    const hasSettledMountRef = useRef(false);
    useEffect(() => {
        if (hasSettledMountRef.current || !isMirroring) {
            return;
        }
        hasSettledMountRef.current = true;

        if (mountState.isForeign || mountState.isOrphaned) {
            // Writing the empty value fills the key back in, and the key
            // standing empty would read as a selection to keep.
            lastSeenKeyRef.current = stableBlobKey(emptyValue);
            setValue(emptyValue);
            storage.removeItem(storageKey);
            clearSyncRecord(storage, storageKey);
            return;
        }

        if (mountState.claimsLocal) {
            writeSyncRecord(storage, storageKey, { owner: ownerId });
        }
    }, [emptyValue, isMirroring, mountState, ownerId, setValue, storage, storageKey]);

    const { data, error } = useQuery<DurableFilterRowQuery, DurableFilterRowQueryVariables>(
        DURABLE_FILTER_ROW_QUERY,
        {
            variables: { pageName: storageKey, filterName: LAST_USED_FILTER_NAME },
            skip: !isMirroring,
            fetchPolicy: 'cache-and-network',
        },
    );

    useEffect(() => {
        // A response that answers without the field reads as no answer, so a
        // partial result leaves the browser's copy on screen.
        const rows = data?.dashboardFilterConfigs;
        if (!isMirroring || rows === undefined) {
            return;
        }

        if (rows.length === 0) {
            // Nothing is stored for this page yet, so whatever the browser
            // holds is what the row should start from.
            if (storage.getItem(storageKey) !== null) {
                syncedKeyRef.current = null;
            }
            return;
        }

        if (hasEditedRef.current) {
            return;
        }

        const [row] = rows;
        const rowSyncedAt = toRowTimestamp(row.updatedAt);
        if (syncedAtRef.current !== null && rowSyncedAt <= syncedAtRef.current) {
            return;
        }

        const restored = fromDurableFilterBlob(row.filterBlob, schema);
        if (restored === null) {
            reportError(new Error(`Unreadable stored filter selection for '${storageKey}'`));
            // A row in this build's envelope that fails the schema was written
            // under an older value shape, so the browser's selection replaces
            // it. A row from another envelope version is left for that build.
            if (isCurrentDurableFilterBlob(row.filterBlob)) {
                pendingRef.current = value;
                flushRef.current();
            }
            return;
        }

        syncedAtRef.current = rowSyncedAt;
        syncedKeyRef.current = stableBlobKey(restored);
        writeSyncRecord(storage, storageKey, {
            owner: ownerId,
            syncedAt: new Date(row.updatedAt).toISOString(),
        });

        if (syncedKeyRef.current !== stableBlobKey(value)) {
            lastSeenKeyRef.current = syncedKeyRef.current;
            setValue(restored);
        }
    }, [data, isMirroring, ownerId, schema, setValue, storage, storageKey, value]);

    const [saveRow] = useMutation<
        SaveDurableFilterRowMutation,
        SaveDurableFilterRowMutationVariables
    >(SAVE_DURABLE_FILTER_ROW_MUTATION);

    const flush = useCallback(() => {
        const pending = pendingRef.current;
        if (pending === null) {
            return;
        }

        pendingRef.current = null;
        // Held so a write that never landed can be un-marked, leaving the
        // selection looking unstored and so eligible to be written again.
        const keyBeforeSave = syncedKeyRef.current;
        syncedKeyRef.current = stableBlobKey(pending);
        saveRow({ variables: { pageName: storageKey, filterBlob: toDurableFilterBlob(pending) } })
            .then((result) => {
                const saved = result.data?.saveDashboardFilterConfig;
                if (saved === undefined || ownerId === null) {
                    return;
                }
                syncedAtRef.current = toRowTimestamp(saved.updatedAt);
                writeSyncRecord(storage, storageKey, {
                    owner: ownerId,
                    syncedAt: new Date(saved.updatedAt).toISOString(),
                });
            })
            .catch((error) => {
                syncedKeyRef.current = keyBeforeSave;
                reportError(error);
            });
    }, [ownerId, saveRow, storage, storageKey]);

    const flushRef = useRef(flush);
    useEffect(() => {
        flushRef.current = flush;
    });

    useEffect(() => {
        const writeBeforeHidden = () => {
            if (document.visibilityState === 'hidden') {
                flushRef.current();
            }
        };

        document.addEventListener('visibilitychange', writeBeforeHidden);
        return () => {
            document.removeEventListener('visibilitychange', writeBeforeHidden);
            flushRef.current();
        };
    }, []);

    useEffect(() => {
        const shouldWrite =
            isMirroring &&
            // The row mirrors what this browser has stored, so nothing is
            // written before the browser holds a selection of its own.
            storage.getItem(storageKey) !== null &&
            // The row is read before it is written, so a selection stored
            // elsewhere is not overwritten by a page that never saw it. A
            // selection the user just made is theirs to overwrite it with.
            (hasEditedRef.current || data !== undefined || error !== undefined) &&
            // A hidden tab is not where the selection is being made, so the
            // visible tab owns the write.
            document.visibilityState !== 'hidden' &&
            stableBlobKey(value) !== syncedKeyRef.current;

        if (!shouldWrite) {
            pendingRef.current = null;
            return;
        }

        pendingRef.current = value;
        const timer = setTimeout(() => flushRef.current(), AUTOSAVE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [data, error, isMirroring, storage, storageKey, value]);
};
